import * as React from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  stockApi,
  type SellerLocationAccess,
  type SellerWorkspaceContextResponse,
} from '@/api/stock';
import { queryKeys } from '@/lib/query-client';

import {
  readStoredSellerLocation,
  sellerLocationStorageKey,
  subscribeStoredSellerLocation,
  writeStoredSellerLocation,
} from './sellerLocationStorage';

type UseSellerWorkspaceLocationParams = {
  accountId: string | null | undefined;
  /** Stores this screen can work with (already filtered for the route). */
  locations: SellerLocationAccess[];
};

/**
 * The store the user works in. The choice is kept on the server per user (owner
 * 10.10.2026: it used to live only in the browser, and a fresh browser storage
 * silently fell back to the first store by name); the browser copy only paints
 * the screen before the server answers. A user with several stores and no
 * choice gets `needsChoice` instead of a silent default.
 */
export function useSellerWorkspaceLocation({
  accountId,
  locations,
}: UseSellerWorkspaceLocationParams) {
  const queryClient = useQueryClient();
  const storageKey = sellerLocationStorageKey(accountId);
  const storedLocationId = React.useSyncExternalStore(
    (onStoreChange) => subscribeStoredSellerLocation(storageKey, onStoreChange),
    () => readStoredSellerLocation(storageKey),
    () => '',
  );
  const context = useQuery({
    queryKey: queryKeys.stock.sellerWorkspace.context(),
    queryFn: () => stockApi.sellerWorkspace.context(),
  });
  const serverLocationId = context.data?.profile.selected_location_id ?? '';
  const locationIds = React.useMemo(
    () => new Set(locations.map((item) => item.location_id)),
    [locations],
  );
  const usable = (id: string) => (id && locationIds.has(id) ? id : '');
  const onlyLocationId = locations.length === 1 ? locations[0]!.location_id : '';
  // A choice exists but this screen cannot use that store (for example receiving
  // runs in one store only): the screen's first store is shown, nothing is saved.
  const elsewhereChoice = serverLocationId || storedLocationId;
  const selectedLocationId =
    usable(serverLocationId) ||
    usable(storedLocationId) ||
    onlyLocationId ||
    (elsewhereChoice ? (locations[0]?.location_id ?? '') : '');
  const needsChoice = context.isSuccess && locations.length > 1 && !selectedLocationId;
  const selectedLocation = React.useMemo(
    () => locations.find((item) => item.location_id === selectedLocationId) ?? null,
    [locations, selectedLocationId],
  );
  const selectLocation = React.useCallback(
    (value: string) => {
      writeStoredSellerLocation(storageKey, value);
      if (!value) return;
      queryClient.setQueryData<SellerWorkspaceContextResponse>(
        queryKeys.stock.sellerWorkspace.context(),
        (current) =>
          current && { ...current, profile: { ...current.profile, selected_location_id: value } },
      );
      // The browser copy already holds the choice; a failed save is retried on the next pick.
      void stockApi.sellerWorkspace.setCurrentLocation(value).catch(() => undefined);
    },
    [queryClient, storageKey],
  );

  return {
    selectedLocationId,
    selectedLocation,
    selectedLocationName: selectedLocation?.location_name ?? '',
    selectLocation,
    needsChoice,
  };
}
